import React, { useState, useCallback, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import { useMutation, useQuery } from "react-query";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import queryString from "query-string";

import { useAuth } from "../../hooks/useAuth";
import { fetchActionsUtil } from "../../utils/helpers";
import EyeOffOutlineIcon from "mdi-react/EyeOffOutlineIcon";
import EyeOutlineIcon from "mdi-react/EyeOutlineIcon";
import AsyncCreatableSelect from "react-select/async-creatable";

const EnableDemoEnvironmentModal = ({
  show,
  onHide,
  partnerId,
  onSuccess,
  demoEnvironmentData,
}) => {
  const { backendUrl, token } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  useEffect(() => {
    if (demoEnvironmentData) {
      setSelectedPartner({
        value: demoEnvironmentData?.partnerId || "",
        label: demoEnvironmentData?.partnerName || "",
        partner: demoEnvironmentData,
        is_verified: demoEnvironmentData?.is_verified || 0,
      });
    }
  }, [demoEnvironmentData]);

  // Fetch partners for async select
  const fetchPartners = useCallback(
    async (search = "") => {
      try {
        const params = {
          page: 1,
          limit: 100,
          companyName: search || "",
        };

        const response = await fetch(
          `${backendUrl}/api/partnership/invexerp-partners?${queryString.stringify(
            params,
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "Application/json",
              "Content-Type": "Application/json",
              Authorization: `Bearer ${token}`,
            },
            credentials: "include",
          },
        );

        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.message);
        }

        const data = await response.json();

        return data?.data || [];
      } catch (error) {
        console.error(
          "EnableDemoEnvironmentModal: failed to load partners",
          error,
        );
        return [];
      }
    },
    [backendUrl, token],
  );

  const loadPartnerOptions = useCallback(
    async (inputValue) => {
      const partners = await fetchPartners(inputValue);

      if (demoEnvironmentData) {
        const partner = partners.find((partner) => partner?.id === partnerId);
        setSelectedPartner((prev) => ({ ...prev, ...partner }));
      }

      const options = partners.map((partner) => ({
        value: partner?.id,
        label: partner?.company_name,
        partner,
        is_verified: partner?.is_verified || 0,
      }));

      return options;
    },
    [fetchPartners],
  );

  const formik = useFormik({
    initialValues: {
      partnerId: partnerId || "",
      partnerName: demoEnvironmentData?.partnerName || "",
      username: demoEnvironmentData?.username || "",
      email: demoEnvironmentData?.email || "",
      password: demoEnvironmentData?.password || "",
      link: demoEnvironmentData?.link || "",
      demoEnvironmentTitle: demoEnvironmentData?.demoEnvironmentTitle || "",
      isActive: demoEnvironmentData?.isActive ?? true,
      id: demoEnvironmentData?.id || "",
    },
    validationSchema: yup.object().shape({
      partnerId: yup.string().required("Partner is required"),
      username: yup.string().required("Username is required"),
      email: yup
        .string()
        .email("Invalid email address")
        .required("Email is required"),
      password: yup.string().required("Password is required"),
      link: yup.string().required("Link is required"),
      demoEnvironmentTitle: yup
        .string()
        .required("Demo environment title is required"),
    }),
    onSubmit: async (values) => {
      const payload = {
        partnerId: values.partnerId,
        partnerName: values.partnerName,
        username: values.username,
        email: values.email,
        password: values.password,
        link: values.link,
        demoEnvironmentTitle: values.demoEnvironmentTitle,
        isActive: values.isActive,
        ...(values?.id ? { id: String(values.id) } : {}),
      };

      if (!selectedPartner?.is_verified) {
        toast.error("Partner is not verified");

        return;
      }
      enableDemoEnvironment.mutate(payload);
    },
  });

  const handlePartnerChange = (selected, { action }) => {
    if (action === "clear" || !selected) {
      setSelectedPartner(null);
      formik.setFieldValue("partnerId", "");
      formik.setFieldValue("partnerName", "");
      return;
    }

    if (selected.__isNew__) {
      toast.error("Please select an existing partner");
      setSelectedPartner(null);
      formik.setFieldValue("partnerId", "");
      formik.setFieldValue("partnerName", "");
      return;
    }

    setSelectedPartner(selected);
    // Update formik values with partner info
    if (selected.partner) {
      formik.setFieldValue("partnerId", selected.partner.id);
      formik.setFieldValue("partnerName", selected.partner.company_name);
      formik.setFieldTouched("partnerId", true);
    }
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      width: "100%",
      minWidth: 0,
      minHeight: "38px",
      borderColor: state.isFocused ? "#0a4a95" : "#ced4da",
      boxShadow: state.isFocused ? "0 0 0 0.1rem rgba(13,110,253,.25)" : "none",
    }),
    menu: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    container: (base) => ({
      ...base,
      width: "100%",
      flex: 1,
      minWidth: 0,
    }),
  };

  const enableDemoEnvironment = useMutation(
    (payload) =>
      fetchActionsUtil(
        `${backendUrl}/api/partnership/post-demo-access-environment`,
        "POST",
        "",
        payload,
      ),
    {
      onSuccess: (data) => {
        toast.success(data.message || "Demo environment enabled successfully");
        formik.resetForm();
        setSelectedPartner(null);
        onSuccess?.();
        onHide();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to enable demo environment");
      },
    },
  );

  const handleClose = () => {
    formik.resetForm();
    setSelectedPartner(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="md">
      <Modal.Header closeButton>
        <Modal.Title as={"h6"}>Assign Demo Environment</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form
          onSubmit={formik.handleSubmit}
          // style={{
          //   pointerEvents: isLoading ? "none" : "",
          // }}
        >
          {/* Partner Select */}
          <Form.Group className="mb-3">
            <Form.Label>Partner</Form.Label>
            <div style={{ width: "100%" }}>
              <AsyncCreatableSelect
                classNamePrefix="partner-select"
                styles={customStyles}
                cacheOptions
                defaultOptions
                loadOptions={loadPartnerOptions}
                value={selectedPartner}
                onChange={handlePartnerChange}
                onBlur={() => formik.setFieldTouched("partnerId", true)}
                placeholder="Search partner..."
                isClearable
                isCreatable={false}
                formatCreateLabel={(inputValue) =>
                  `Create new partner: "${inputValue}"`
                }
                loadingMessage={() => "Loading partners..."}
                noOptionsMessage={({ inputValue }) =>
                  inputValue
                    ? `No partner found for "${inputValue}"`
                    : "No partners"
                }
              />
            </div>
            {formik.touched.partnerId && formik.errors.partnerId && (
              <div className="text-danger mt-1">{formik.errors.partnerId}</div>
            )}
          </Form.Group>

          {/* Demo Environment Title */}
          <Form.Group className="mb-3">
            <Form.Label>Demo Environment Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter demo environment title"
              name="demoEnvironmentTitle"
              value={formik.values.demoEnvironmentTitle}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.demoEnvironmentTitle &&
                Boolean(formik.errors.demoEnvironmentTitle)
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.demoEnvironmentTitle}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Username</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter username"
              name="username"
              value={formik.values.username}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.username && Boolean(formik.errors.username)
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.username}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              placeholder="Enter email"
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.email && Boolean(formik.errors.email)}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.email}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3 position-relative">
            <Form.Label>Password</Form.Label>
            <Form.Control
              type={showPassword ? "text" : "password"}
              placeholder="Enter password"
              name="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={
                formik.touched.password && Boolean(formik.errors.password)
              }
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.password}
            </Form.Control.Feedback>

            <Button
              variant=""
              type="button"
              style={{
                position: "absolute",
                right: 0,
                top: "2rem",
                transform: `scale(0.8)`,
              }}
              onClick={(e) => {
                e.target.blur();
                setShowPassword(!showPassword);
              }}
              className="no-focus text-light"
            >
              {!showPassword ? <EyeOffOutlineIcon /> : <EyeOutlineIcon />}
            </Button>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Link</Form.Label>
            <Form.Control
              type="url"
              placeholder="Enter demo environment link"
              name="link"
              value={formik.values.link}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.link && Boolean(formik.errors.link)}
            />
            <Form.Control.Feedback type="invalid">
              {formik.errors.link}
            </Form.Control.Feedback>
          </Form.Group>

          {/* Active/Inactive Checkbox */}
          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              id="isActive"
              name="isActive"
              label="Active"
              checked={formik.values.isActive}
              onChange={(e) => {
                formik.setFieldValue("isActive", e.target.checked);
              }}
            />
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={enableDemoEnvironment.isLoading}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={formik.handleSubmit}
          disabled={enableDemoEnvironment.isLoading}
        >
          {enableDemoEnvironment.isLoading
            ? "Assigning..."
            : "Assign Demo Environment"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EnableDemoEnvironmentModal;
